import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WaAppDetailsPage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN may change anything here; everyone else gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageClientSetup } = useUserProfileAccess();
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newApp, setNewApp] = useState({
    waAppId: '',
    waAppName: '',
    status: false,
  });

  const [editWaAppName, setEditWaAppName] = useState('');
  const [editingAppId, setEditingAppId] = useState(null);
  const [actionErrors, setActionErrors] = useState({});
  const [syncingAuthAppId, setSyncingAuthAppId] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const initialFilters = {
    id: '',
    waAppId: '',
    waAppName: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(0);
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(3);
  const [formError, setFormError] = useState({});

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
    setEditingAppId(null);
  };

  useEffect(() => {

    const fetchApps = async () => {
      try {
        // WAMetaAppDetailController only exposes a cached full list, not a paged search,
        // so filtering and pagination for this page are done client-side.
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_META_APP_DETAILS_ENDPOINT
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setAllApps(object || []);
        } else {
          setError(`Error: ${message}`);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching app details:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    fetchApps();
  }, [waApi]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewApp((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const applyFilters = () => {
    clearMessages();
    setAppliedFilters(filters);
    setPage(0);
  };

  const filteredApps = allApps.filter((app) => {
    if (appliedFilters.id && String(app.id) !== String(appliedFilters.id)) return false;
    if (appliedFilters.waAppId && !app.waAppId?.toLowerCase().includes(appliedFilters.waAppId.toLowerCase())) return false;
    if (appliedFilters.waAppName && !app.waAppName?.toLowerCase().includes(appliedFilters.waAppName.toLowerCase())) return false;
    if (appliedFilters.status !== null && app.status !== appliedFilters.status) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / size));
  const paginatedApps = filteredApps.slice(page * size, page * size + size);

  const handleEditClick = (app) => {
    clearMessages();
    setEditingAppId(app.id);
    setEditWaAppName(app.waAppName || "");
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_META_APP_DETAIL_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_META_APP_DETAIL_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setAllApps((prev) =>
        prev.map((app) => app.id === id ? { ...app, status: newStatus } : app)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleSyncAuth = async (app) => {
    clearMessages();
    setSyncingAuthAppId(app.waAppId);
    try {
      const response = await waApi.get(
        `${process.env.REACT_APP_SYNC_WA_META_APP_AUTH_ENDPOINT}/${app.waAppId}`
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [app.id]: err.message || 'An error occurred while syncing auth.' }));
    } finally {
      setSyncingAuthAppId(null);
    }
  };

  const handleEditSubmit = async (id) => {
    clearMessages();
    try {
      const response = await waApi.put(
        `${process.env.REACT_APP_PUT_WA_META_APP_DETAIL_ENDPOINT}/${id}`,
        {
          waAppName: editWaAppName
        }
      );

      setAllApps((prev) =>
        prev.map((app) => app.id === id ? { ...app, waAppName: editWaAppName } : app)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the App Detail.' }));
    } finally {
      setEditingAppId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this app detail?')) {
      return;
    }
    setActionErrors((prev) => ({ ...prev, [id]: null }));
    try {
      await waApi.delete(`${process.env.REACT_APP_DELETE_WA_META_APP_DETAIL_ENDPOINT}/${id}`);
      setAllApps((prev) => prev.filter((app) => app.id !== id));
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while deleting the  App Detail.' }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
      waAppId: { value: newApp.waAppId, label: "App Id" },
      waAppName: { value: newApp.waAppName, label: "App Name" },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }
    try {
      const payload = deepTrim(newApp);
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_META_APP_DETAIL_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setAllApps((prev) => [...prev, response.data.object]);
        setNewApp({ waAppId: '', waAppName: '', status: true });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the app detail.');
    } finally {
      setModalOpen(false);
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    setSize(Number(e.target.value));
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading app details: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Details</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>App Id:</label>
          <input
            type="text"
            name="waAppId"
            value={filters.waAppId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>App Name:</label>
          <input
            type="text"
            name="waAppName"
            value={filters.waAppName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {canManageClientSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewApp({ waAppId: '', waAppName: '', status: false });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New App Detail</h3>
            <div className="form-group">
              <label>App Id:</label>
              <input
                type="text"
                name="waAppId"
                value={newApp.waAppId}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>App Name:</label>
              <input
                type="text"
                name="waAppName"
                value={newApp.waAppName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newApp.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create App</button>
          </form>
        </div>
      </Modal>

      <h3>Existing App Details</h3>
      {paginatedApps.length === 0 ? (
        <p>No app details available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>App Id</th>
                <th>App Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageClientSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedApps.map((app) => (
                <tr key={app.id}>
                  <td>{app.id}</td>
                  <td>{app.waAppId}</td>
                  <td>
                    {editingAppId === app.id ? (
                      <input
                        type="text"
                        value={editWaAppName}
                        onChange={(e) => setEditWaAppName(e.target.value)}
                      />
                    ) : (
                      app.waAppName
                    )}
                  </td>
                  <td>{app.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(app.createdTimestamp).toLocaleString()}</td>
                  <td>{new Date(app.updatedTimestamp).toLocaleString()}</td>
                  <td>{app.lastUpdatedBy}</td>
                  {canManageClientSetup && (
                    <td>
                      <div className="action-buttons">
                        {editingAppId === app.id ? (
                          <>
                            <button onClick={() => handleEditSubmit(app.id)}>Save</button>
                            <button type="button" onClick={clearMessages}>Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => handleEditClick(app)}>Edit</button>
                        )}
                        <button onClick={() => toggleActivationStatus(app.id, app.status)}>
                          {app.status ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleSyncAuth(app)}
                          disabled={syncingAuthAppId === app.waAppId}
                        >
                          {syncingAuthAppId === app.waAppId ? 'Syncing...' : 'Sync Auth'}
                        </button>
                        {/* <button onClick={() => handleDelete(app.id)}>Delete</button> */}
                      </div>
                      {actionErrors[app.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[app.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaAppDetailsPage;
